import { ChangeDetectionStrategy, Component, DestroyRef, signal } from '@angular/core';
import { LowerCasePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { TranslocoPipe } from '@jsverse/transloco';
import { IconComponent } from '@examdojo/core/icon';
import { delay, finalize, first, of, switchMap, tap } from 'rxjs';
import { MatTooltip } from '@angular/material/tooltip';
import { SmallLayoutComponent, WideLayoutComponent } from '@examdojo/layout';
import {
  AnimationDuration,
  getAnimationAppearDisappear,
  getAnimationAppearDisappearWithHeight,
} from '@examdojo/animation';
import { AbstractDialogService } from '@examdojo/core/dialog';
import { ActivityQuery, OngoingActivity } from '../../activity/activity.query';
import { ChallengeDialogService } from '../../features/exams/dialog/challenge-dialog.service';
import { HomeworkActivityDialogService } from '../../features/homework/dialog/homework-activity-dialog.service';
import { TopicPracticeDialogService } from '../../features/practice/topic-practice/dialog/topic-practice-dialog.service';
import { CourseManagementService } from '../course-management/course-management.service';
import { ActivityType } from '../activity-conflict';

@Component({
  selector: 'dojo-resume-activity-button',
  imports: [TranslocoPipe, IconComponent, MatTooltip, WideLayoutComponent, SmallLayoutComponent, LowerCasePipe],
  templateUrl: './resume-activity-button.component.html',
  styleUrl: './resume-activity-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    getAnimationAppearDisappear({ enter: AnimationDuration.Short, leave: AnimationDuration.Short }),
    getAnimationAppearDisappearWithHeight({ enter: AnimationDuration.Short, leave: AnimationDuration.Short }),
  ],
})
export class ResumeActivityButtonComponent {
  constructor(
    private readonly activityQuery: ActivityQuery,
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly topicPracticeDialogService: TopicPracticeDialogService,
    private readonly challengeDialogService: ChallengeDialogService,
    private readonly homeworkActivityDialogService: HomeworkActivityDialogService,
    private readonly courseManagementService: CourseManagementService,
  ) {}

  private readonly canShowResumeButton$ = this.activityQuery.hasOngoingActivity$.pipe(
    switchMap((hasActivity) => (hasActivity ? of(true).pipe(delay(500)) : of(false))),
  );

  readonly state = connectState({
    ongoingActivity: this.activityQuery.ongoingActivity$,
    canShowResumeButton: this.canShowResumeButton$,
  });

  private readonly isResuming = signal(false);

  resumeActivity(): void {
    if (this.isResuming()) {
      return;
    }

    const activity = this.state.ongoingActivity;
    if (!activity) {
      return;
    }

    const dialogService = this.getDialogServiceForActivity(activity.type);
    if (!dialogService || dialogService.isOpen()) {
      return;
    }

    this.isResuming.set(true);

    this.restoreCourseContextWhenDialogPresented(dialogService, activity);
    dialogService.openDialog();
  }

  private restoreCourseContextWhenDialogPresented(
    dialogService: AbstractDialogService,
    activity: OngoingActivity,
  ): void {
    dialogService.dialogPresented$
      .pipe(
        first(),
        tap(() => this.router.navigate([activity.route])),
        switchMap(() =>
          this.courseManagementService.restoreCourseContext({
            syllabusId: activity.syllabusId,
            courseLevel: activity.courseLevel,
          }),
        ),
        finalize(() => this.isResuming.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private getDialogServiceForActivity(type: ActivityType): AbstractDialogService | undefined {
    switch (type) {
      case 'practice':
        return this.topicPracticeDialogService;
      case 'challenge':
        return this.challengeDialogService;
      case 'homework':
        return this.homeworkActivityDialogService;
    }
  }
}
